/* eslint-disable no-console */
import _merge from 'lodash/merge';
import { Lightning, Router } from '@lightningjs/sdk';
import { pairing } from '@bedrock-rdk/config';
import Store from 'lightningjs-redux';
import { t } from '../../../i18n';
import { PairingSelector } from '../../../tornado/pairing/selector/pairingSelector.component';
import { DesktopPairing } from '../../../tornado/pairing/desktop/desktopPairing.component';
import { MobilePairing } from '../../../tornado/pairing/mobile/mobilePairing.component';
import { HIDE_POPUP_SIGNAL } from '../../../popupContainer/popupContainer.constants';
import { fetchUserSubscription } from '../../../../logic/pairing/pairing.actions';
import { SELECTED_SIGNAL } from '../../../tornado/signals';
import { setDeeplinkLocation } from '../../../../logic/routing/routing.actions';
import { getNestedOriginalTarget, getUnlockPathPrefix, isPlayerLocked } from '../../../../logic/lock/lock.utils';
import { BrandIcon } from '../../../tornado/brand-icon/brandIcon.component';
import { navigateTo, PAIRING_PATH } from '../../../routes/routes.utils';
import { sendEvent } from '../../../../logic/analytics/analytics.utils';
import { fetchLayout } from '../../../../logic/layout/layout.actions';
import { removeSplashScreen } from '../../../../logic/app/app.utils';
import { createItemAction } from '../../../cta/cta.actions';
import { getInitialStyle, defaultContext } from './subscriptionExpiredBase.style';
import connect from './subscriptionExpiredBase.connector';

/**
 * SubscriptionExpired page
 *
 * @version 1.2.1
 * @see https://zeroheight.com/726d5c282/p/00d378-instruction-page--v122/b/83ab7b/t/19f2d6128a
 */
export class SubscriptionExpiredBase extends Lightning.Component {
  static _template() {
    return _merge(
      {
        Background: {
          rect: true,
        },
        CustomerIcon: {
          type: BrandIcon,
        },
        Left: {
          type: PairingSelector,
          buttons: [
            {
              label: t('pairing.token.mobile.button'),
              iconLeft: 'QRCode',
              iconRight: 'ArrowRight',
            },
            {
              label: t('pairing.token.computer.button'),
              iconLeft: 'Laptop',
              iconRight: 'ArrowRight',
            },
            {
              label: t('pairing.subscribe.offer.back'),
            },
          ],
          labels: {
            features: [
              t('pairing.subscribe.offers.offer1'),
              t('pairing.subscribe.offers.offer2'),
              t('pairing.subscribe.offers.offer3'),
            ],
            title: t('pairing.subscribe.offers.title'),
          },
          signals: {
            [SELECTED_SIGNAL]: '_switchViews',
          },
        },
        Right: {
          Mobile: {
            type: MobilePairing,
            labels: {
              title: t('pairing.subscribe.how'),
              steps: 'pairing.subscribe.mobile.steps.step',
              stepsCount: 3,
            },
            button: t('pairing.subscribe.button'),
            url: pairing.subscriptionURL,
            signals: {
              [SELECTED_SIGNAL]: '_handleUnlockAttempt',
            },
          },
          Desktop: {
            type: DesktopPairing,
            labels: {
              title: t('pairing.subscribe.how'),
              steps: 'pairing.subscribe.computer.steps.step',
              stepsCount: 3,
            },
            button: t('pairing.subscribe.button'),
            signals: {
              [SELECTED_SIGNAL]: '_handleUnlockAttempt',
            },
          },
        },
      },
      getInitialStyle(),
    );
  }

  static _states() {
    return [
      class Left extends this {
        _getFocused() {
          return this.tag('Left');
        }

        _handleRight() {
          this._setState('Right');

          return true;
        }
      },
      class Right extends this {
        _getFocused() {
          return this._views.map(id => this.tag(id)).find(view => view.visible);
        }

        _handleLeft() {
          this._setState('Left');

          return true;
        }
      },
    ];
  }

  _construct() {
    this._style = defaultContext;
    this._focusedButton = 0;
    this._lock = null;
    this._views = ['Mobile', 'Desktop'];
  }

  _build() {
    this.tag(this._views[0]).visible = true;
  }

  _active() {
    this._unsubscribe = connect(this);
    this._isLoading = true;
    removeSplashScreen();

    this._setState('Left');
  }

  _inactive() {
    this._isLoading = false;
    this._lock = null;
    if (this._unsubscribe) {
      this._unsubscribe();
      this._unsubscribe = null;
    }

    if (this._popupHidingTimeout) {
      clearTimeout(this._popupHidingTimeout);
    }
  }

  _switchViews(index) {
    if (index >= this._views.length) {
      this._hideScreen();

      return;
    }

    this._views.forEach((view, i) => {
      this.tag(view).visible = i === index;
    });
  }

  set isPaired(value) {
    this._isPaired = value;
  }

  set lock(value) {
    this._lock = value;
  }

  set payload(value) {
    this._payload = value;
  }

  set services(value) {
    this._services = value;
  }

  set requiredFeatures(value) {
    this._requiredFeatures = value;
  }

  set entityType(value) {
    this._entityType = value;
  }

  set entityId(value) {
    this._entityId = value;
  }

  set deviceBrand(value) {
    this._deviceBrand = value;
  }

  _getOriginalTarget() {
    if (this._lock) {
      const target = getNestedOriginalTarget(this._lock.originalTarget);

      if (target) {
        console.log(`[Upsell] Setting deeplink location basing on lock: ${target}`, this._lock);

        return target;
      }
    }

    if (this._entityType && this._entityId) {
      const prefix = getUnlockPathPrefix(this._entityType);
      const target = `${prefix}${this._entityType}/${this._entityId}`;
      console.log(`[Upsell] Setting deeplink location basing on entity: ${target}`);

      return target;
    }

    return null;
  }

  async _handleUnlockAttempt() {
    if (!this._isPaired) {
      const target = this._getOriginalTarget();
      if (target) {
        Store.dispatch(setDeeplinkLocation(target));
      }

      navigateTo(PAIRING_PATH);
      // The navigation is async though there is no promise, awaiting for a few milliseconds to be sure there will be
      // no flash of the content page behind the modal
      this._popupHidingTimeout = setTimeout(() => {
        this.signal(HIDE_POPUP_SIGNAL);
      }, 100);

      return;
    }

    if (this._payload || this._entityType) {
      try {
        await Store.dispatch(fetchUserSubscription());
      } finally {
        // NOTE: in case the component became inactive, there is no reason to enter this phase
        if (this._isLoading) {
          this._resolveLock();
        }
      }
    }
  }

  _resolveLock() {
    if (this._payload) {
      Store.dispatch(createItemAction(this._payload));
    } else if (this._entityType && this._entityId) {
      Store.dispatch(fetchLayout(this._entityType, this._entityId));
    }

    this.signal(HIDE_POPUP_SIGNAL);
  }

  _hideScreen() {
    if (this._lock) {
      this.signal(HIDE_POPUP_SIGNAL);

      if (isPlayerLocked(this._entityType)) {
        Router.back();
      }
    } else {
      Router.back();
    }
  }

  _handleBack() {
    sendEvent({ key: 'pressTokenBackButton' });

    return false;
  }
}
